import { ChevronDown } from 'lucide-react';

import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';

const inputSurfaceClass =
    'box-border h-11 w-full rounded-md border border-solid border-[hsl(var(--border))] bg-[hsl(var(--input))] py-0 font-inter leading-normal text-foreground shadow-none focus:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * Checkout text field: label above, themed input, helper / error text.
 * Supports controlled (value) or uncontrolled (defaultValue + inputRef) for address autocomplete.
 */
export function CheckoutInputField({
    id,
    label,
    value,
    defaultValue,
    inputRef,
    error,
    message,
    onChange,
    className,
    disabled,
    ...inputProps
}) {
    const describedBy = message ? `${id}-description` : undefined;

    return (
        <div className={cn('w-full', className)}>
            {label ? (
                <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-foreground">
                    {label}
                </label>
            ) : null}
            {defaultValue !== undefined && inputRef !== undefined ? (
                <Input
                    {...inputProps}
                    ref={inputRef}
                    id={id}
                    name={id}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    onChange={onChange}
                    className={cn(
                        inputSurfaceClass,
                        error && 'border-destructive focus:border-destructive',
                        inputProps.className,
                    )}
                    aria-invalid={Boolean(error)}
                    aria-describedby={describedBy}
                />
            ) : (
                <Input
                    {...inputProps}
                    id={id}
                    name={id}
                    value={value ?? ''}
                    disabled={disabled}
                    onChange={onChange}
                    className={cn(inputSurfaceClass, error && 'border-destructive focus:border-destructive', inputProps.className)}
                    aria-invalid={Boolean(error)}
                    aria-describedby={describedBy}
                />
            )}
            {message ? (
                <p
                    id={describedBy}
                    className={cn('mt-1.5 text-sm', error ? 'text-destructive' : 'text-muted-foreground')}
                    role={error ? 'alert' : undefined}
                >
                    {message}
                </p>
            ) : null}
        </div>
    );
}

/**
 * Native select with same surface as checkout inputs.
 */
export function CheckoutSelectField({
    id,
    label,
    value,
    error,
    message,
    onChange,
    className,
    disabled,
    children,
}) {
    const describedBy = message ? `${id}-description` : undefined;

    return (
        <div className={cn('w-full', className)}>
            {label ? (
                <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-foreground">
                    {label}
                </label>
            ) : null}
            <div className="relative">
                <select
                    id={id}
                    name={id}
                    value={value}
                    disabled={disabled}
                    onChange={onChange}
                    className={cn(
                        inputSurfaceClass,
                        'cursor-pointer appearance-none px-3 pr-10',
                        error && 'border-destructive',
                        disabled && 'cursor-not-allowed opacity-50',
                    )}
                    aria-invalid={Boolean(error)}
                    aria-describedby={describedBy}
                >
                    {children}
                </select>
                <ChevronDown
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                />
            </div>
            {message ? (
                <p
                    id={describedBy}
                    className={cn('mt-1.5 text-sm', error ? 'text-destructive' : 'text-muted-foreground')}
                    role={error ? 'alert' : undefined}
                >
                    {message}
                </p>
            ) : null}
        </div>
    );
}
